import React from 'react';
import { Box, Typography, LinearProgress, Fade, Zoom, CircularProgress } from '@mui/material';
import { keyframes } from '@emotion/react';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import type { TranslationKeys } from '../i18n';

const pulse = keyframes`
  0% {
    transform: scale(1);
    opacity: 0.8;
  }
  50% {
    transform: scale(1.05);
    opacity: 1;
  }
  100% {
    transform: scale(1);
    opacity: 0.8;
  }
`;

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const float = keyframes`
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-10px);
  }
`;

const shimmer = keyframes`
  0% {
    background-position: -1000px 0;
  }
  100% {
    background-position: 1000px 0;
  }
`;

const rotate = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const LoadingContainer = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  height: '100vh',
  width: '100vw',
  position: 'fixed',
  top: 0,
  left: 0,
  overflow: 'hidden',
  background: 'radial-gradient(circle at center, #c4262e 0%, #a21e26 50%, #7a161c 100%)',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `
      radial-gradient(circle at 20% 30%, rgba(196, 38, 46, 0.15) 0%, transparent 50%), 
      radial-gradient(circle at 80% 70%, rgba(45, 54, 58, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 40% 80%, rgba(196, 38, 46, 0.08) 0%, transparent 50%)
    `,
    animation: `${float} 8s ease-in-out infinite`,
  }
}));

const LogoContainer = styled(Box)(() => ({
  position: 'relative',
  zIndex: 1,
  animation: `${pulse} 3s ease-in-out infinite`,
}));

const ContentContainer = styled(Box)(() => ({
  position: 'relative',
  zIndex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 2,
  animation: `${fadeInUp} 1.2s ease-out`,
  padding: '0 20px',
  maxWidth: '90vw',
  textAlign: 'center',
}));

const StyledLinearProgress = styled(LinearProgress)(() => ({
  width: '300px',
  maxWidth: '85vw',
  height: '6px',
  borderRadius: '3px',
  backgroundColor: 'rgba(255, 255, 255, 0.2)',
  '& .MuiLinearProgress-bar': {
    backgroundColor: '#ffffff',
    borderRadius: '3px',
    boxShadow: '0 0 10px rgba(255, 255, 255, 0.3)',
    background: `linear-gradient(90deg, #ffffff 0%, #f0f0f0 50%, #ffffff 100%)`,
    backgroundSize: '200% 100%',
    animation: `${shimmer} 2s infinite`,
  }
}));

const FloatingDot = styled(Box)<{ delay: number; size: number; top: string; left: string }>(({ delay, size, top, left }) => ({
  position: 'absolute',
  width: `${Math.min(size, 60)}px`,
  height: `${Math.min(size, 60)}px`,
  backgroundColor: 'rgba(45, 54, 58, 0.15)',
  borderRadius: '50%',
  top,
  left,
  animation: `${float} 6s ease-in-out infinite`,
  animationDelay: `${delay}s`,
  '@media (max-width: 768px)': {
    width: `${Math.min(size * 0.7, 40)}px`,
    height: `${Math.min(size * 0.7, 40)}px`,
  }
}));

const ProcessingIndicator = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 2,
  marginTop: '16px',
  '& .MuiCircularProgress-root': {
    animation: `${rotate} 1.5s linear infinite`,
  }
}));

const StatusDot = styled(Box)<{ active: boolean }>(({ active }) => ({
  width: '10px',
  height: '10px',
  borderRadius: '50%',
  backgroundColor: active ? '#ffffff' : 'rgba(255, 255, 255, 0.3)',
  transition: 'all 0.3s ease',
  boxShadow: active ? '0 0 10px rgba(255, 255, 255, 0.5)' : 'none',
}));

interface AnimatedLoginScreenProps {
  message?: string;
  stage?: 'initializing' | 'authenticating' | 'loading_profile' | 'finalizing';
}

const AnimatedLoginScreen: React.FC<AnimatedLoginScreenProps> = ({ 
  message,
  stage = 'authenticating'
}) => {
  const { t } = useTranslation<TranslationKeys>();

  const getStageMessage = () => {
    if (message) return message;
    
    switch (stage) {
      case 'initializing':
        return 'Initializing application...';
      case 'authenticating':
        return 'Authenticating with Microsoft...';
      case 'loading_profile':
        return 'Loading your profile...';
      case 'finalizing':
        return 'Preparing your workspace...';
      default:
        return t('signing_in');
    }
  };

  const stages = [
    { key: 'initializing', label: 'Initialize' },
    { key: 'authenticating', label: 'Authenticate' },
    { key: 'loading_profile', label: 'Load Profile' },
    { key: 'finalizing', label: 'Finalize' }
  ];

  const currentStageIndex = stages.findIndex(s => s.key === stage);
  
  return (
    <LoadingContainer>
      {/* Floating background elements */}
      <FloatingDot delay={0} size={50} top="8%" left="8%" />
      <FloatingDot delay={1.5} size={35} top="15%" left="85%" />
      <FloatingDot delay={3} size={65} top="75%" left="10%" />
      <FloatingDot delay={0.8} size={25} top="85%" left="88%" />
      <FloatingDot delay={2.2} size={45} top="65%" left="75%" />
      <FloatingDot delay={1} size={30} top="25%" left="25%" />
      <FloatingDot delay={2.5} size={40} top="50%" left="5%" />
      <FloatingDot delay={1.8} size={55} top="40%" left="90%" />

      <ContentContainer>
        <Zoom in timeout={1000}>
          <LogoContainer>
            <Box
              sx={{
                width: { xs: 110, sm: 130 },
                height: { xs: 110, sm: 130 },
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #ffffff 0%, rgba(255,255,255,0.95) 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 20px 40px rgba(0,0,0,0.3), 0 5px 15px rgba(196,38,46,0.3)',
                mb: { xs: 2.5, sm: 3.5 },
                border: '4px solid rgba(255, 255, 255, 0.15)',
                position: 'relative',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  inset: -8,
                  borderRadius: '50%',
                  padding: '4px',
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.3), transparent)',
                  WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                  WebkitMaskComposite: 'xor',
                  maskComposite: 'exclude',
                }
              }}
            >
              <Typography
                variant="h2"
                sx={{
                  fontWeight: 900,
                  color: '#2d363a',
                  fontFamily: '"Roboto", sans-serif',
                  letterSpacing: '-2px',
                  fontSize: { xs: '2rem', sm: '3.5rem' },
                  textShadow: '2px 2px 4px rgba(0,0,0,0.1)',
                }}
              >
                BCT
              </Typography>
            </Box>
          </LogoContainer>
        </Zoom>

        <Fade in timeout={1200}>
          <Typography
            variant="h4"
            sx={{
              color: 'white',
              fontWeight: 700,
              textAlign: 'center',
              mb: 0.5,
              textShadow: '0 3px 6px rgba(0, 0, 0, 0.4)',
              fontSize: { xs: '1.6rem', sm: '2.25rem' },
              letterSpacing: '0.5px',
            }}
          >
            Business Case Tracker
          </Typography>
        </Fade>

        <Fade in timeout={1400}>
          <Typography
            variant="body2"
            sx={{
              color: 'rgba(255, 255, 255, 0.85)',
              fontWeight: 400,
              textAlign: 'center',
              mb: { xs: 2, sm: 3 },
              textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)',
              fontSize: { xs: '0.9rem', sm: '1rem' },
            }}
          >
            Edwards Vaccum
          </Typography>
        </Fade>

        <Fade in timeout={1600}>
          <Typography
            variant="h6"
            sx={{
              color: 'rgba(255, 255, 255, 0.95)',
              fontWeight: 400,
              textAlign: 'center',
              mb: { xs: 3, sm: 4 },
              textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
              fontSize: { xs: '1.05rem', sm: '1.35rem' },
              minHeight: '32px',
            }}
          >
            {getStageMessage()}
          </Typography>
        </Fade>

        <Fade in timeout={2000}>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            gap: 3,
            width: '100%',
            maxWidth: '450px'
          }}>
            <StyledLinearProgress />
            
            {/* Stage Progress Indicators */}
            <Box sx={{ 
              display: 'flex', 
              gap: 2, 
              alignItems: 'center',
              justifyContent: 'center',
              flexWrap: 'wrap'
            }}>
              {stages.map((s, index) => (
                <Box key={s.key} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <StatusDot active={index <= currentStageIndex} />
                  <Typography
                    variant="caption"
                    sx={{
                      color: index <= currentStageIndex 
                        ? 'rgba(255, 255, 255, 0.95)' 
                        : 'rgba(255, 255, 255, 0.5)',
                      fontWeight: index === currentStageIndex ? 600 : 400,
                      fontSize: { xs: '0.7rem', sm: '0.75rem' },
                      transition: 'all 0.3s ease',
                    }}
                  >
                    {s.label}
                  </Typography>
                </Box>
              ))}
            </Box>

            <ProcessingIndicator>
              <CircularProgress 
                size={20} 
                thickness={4}
                sx={{ 
                  color: 'rgba(255, 255, 255, 0.9)',
                  '& .MuiCircularProgress-circle': {
                    strokeLinecap: 'round',
                  }
                }} 
              />
              <Typography
                variant="body2"
                sx={{
                  color: 'rgba(255, 255, 255, 0.85)',
                  textAlign: 'center',
                  fontWeight: 300,
                  fontSize: { xs: '0.8rem', sm: '0.875rem' },
                  fontStyle: 'italic',
                }}
              >
                Please wait...
              </Typography>
            </ProcessingIndicator>

            <Typography
              variant="caption"
              sx={{
                color: 'rgba(255, 255, 255, 0.7)',
                textAlign: 'center',
                fontWeight: 300,
                fontSize: { xs: '0.75rem', sm: '0.8rem' },
                px: 2,
                mt: 1,
              }}
            >
              Securing your connection with Microsoft Azure
            </Typography>
          </Box>
        </Fade>
      </ContentContainer>
    </LoadingContainer>
  );
};

export default AnimatedLoginScreen;